import React from 'react';

import moment from 'moment';

import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';

import { convertPhoneNumberToE164, formatDate } from '../../../util/formatters';
import { ICON_NAMES, MaterialDesignIcon } from '../../../util/material-design-icons';
import { isEmail } from '../../../util/string-validators';
import { TableHeaderCell } from '../../../widgets/table-components/table-header-cell';

type TeamEngineDataFieldOption = {
  fieldName: string;
  displayName: string;
  description: string;
  allowMultipleSelection: boolean;
};
const FIELD_MAP: { [fieldName: string]: TeamEngineDataFieldOption } = {
  ignore: {
    fieldName: 'ignore',
    displayName: 'Ignore this field',
    description: '',
    allowMultipleSelection: true,
  },
  name: {
    fieldName: 'name',
    displayName: 'Full Name',
    description: '',
    allowMultipleSelection: false,
  },
  firstName: {
    fieldName: 'firstName',
    displayName: 'First Name',
    description: '',
    allowMultipleSelection: false,
  },
  lastName: {
    fieldName: 'lastName',
    displayName: 'Last Name',
    description: '',
    allowMultipleSelection: false,
  },
  nickname: {
    fieldName: 'nickname',
    displayName: 'Nickname',
    description: '',
    allowMultipleSelection: false,
  },
  email: {
    fieldName: 'email',
    displayName: 'Email',
    description: '',
    allowMultipleSelection: false,
  },
  phoneNumber: {
    fieldName: 'phoneNumber',
    displayName: 'Phone Number',
    description: '',
    allowMultipleSelection: false,
  },
  languagePreference: {
    fieldName: 'languagePreference',
    displayName: 'Language Preference',
    description: '',
    allowMultipleSelection: false,
  },
  jobTitle: {
    fieldName: 'jobTitle',
    displayName: 'Job Title',
    description: '',
    allowMultipleSelection: false,
  },
  startDate: {
    fieldName: 'startDate',
    displayName: 'Start Date',
    description: '',
    allowMultipleSelection: false,
  },
  birthdate: {
    fieldName: 'birthdate',
    displayName: 'Birthdate',
    description: '',
    allowMultipleSelection: false,
  },
  supervisorName: {
    fieldName: 'supervisorName',
    displayName: 'Supervisor Full Name',
    description: '',
    allowMultipleSelection: false,
  },
  supervisorFirstName: {
    fieldName: 'supervisorFirstName',
    displayName: 'Supervisor First Name',
    description: '',
    allowMultipleSelection: false,
  },
  supervisorLastName: {
    fieldName: 'supervisorLastName',
    displayName: 'Supervisor Last Name',
    description: '',
    allowMultipleSelection: false,
  },
  groupName: {
    fieldName: 'groupName',
    displayName: 'Group',
    description: '',
    allowMultipleSelection: true,
  },
  externalId: {
    fieldName: 'externalId',
    displayName: 'Employee Identifier',
    description: '',
    allowMultipleSelection: false,
  },
};
const FIELDS: Array<TeamEngineDataFieldOption> = Object.values(FIELD_MAP);
/**
 * This map contains which fields can't be used together
 */
const INCOMPATIBLE_FIELDS: { [fieldName: string]: Array<string> } = {
  name: ['firstName', 'lastName'],
  firstName: ['name'],
  lastName: ['name'],

  supervisorName: ['supervisorFirstName', 'supervisorLastName'],
  supervisorFirstName: ['supervisorName'],
  supervisorLastName: ['supervisorName'],
};

const removeInvalidAndCorrectCase = (splitResult: Array<string>) =>
  splitResult
    .filter((nameFragment) => !(nameFragment === '' || nameFragment.replace(/\W/g, '').length < 1))
    .map((word) => {
      // Return any two letter name as it's written - typically things like DJ or TJ or whatever
      if (word.length === 2) {
        return word;
      }

      // If a word is entirely upper-cased, then uppercase the first letter but lowercase the rest
      if (word === word.toUpperCase()) {
        return word.charAt(0).toUpperCase() + word.substring(1).toLowerCase();
      }

      // If the first letter isn't capitalized, capitalize it
      if (!/^[A-Z]/.test(word)) {
        return word.charAt(0).toUpperCase() + word.substring(1);
      }

      // Otherwise, this part of the name should be good to go
      return word;
    });

const parseFullName =
  (firstNameFieldName: string, lastNameFieldName: string) => (name: string | number | null) => {
    if (!name) {
      if (firstNameFieldName === 'firstName' || lastNameFieldName === 'lastName') {
        return {
          errorMessage: 'Name is missing for this row. Employee will not be imported.',
        };
      }
      return {
        [firstNameFieldName]: '',
        [lastNameFieldName]: '',
      };
    }

    if (typeof name !== 'string' && Number.isNaN(name)) {
      return {
        errorMessage: `Name failed to parse correctly: ${name}. Employee will not be imported.`,
      };
    }

    const nameString = name.toString();
    if (nameString.includes(',')) {
      const commaSplitResult = removeInvalidAndCorrectCase(nameString.split(','));
      if (commaSplitResult.length >= 2) {
        return {
          [firstNameFieldName]: removeInvalidAndCorrectCase(commaSplitResult[1].split(/\s+/g)).join(
            ' ',
          ),
          [lastNameFieldName]: commaSplitResult[0].trim(),
        };
      }
    } else {
      const spaceSplitResult = removeInvalidAndCorrectCase(nameString.split(/\s+/g));
      if (spaceSplitResult.length > 1) {
        return {
          [firstNameFieldName]: spaceSplitResult.slice(0, spaceSplitResult.length - 1).join(' '),
          [lastNameFieldName]: spaceSplitResult[spaceSplitResult.length - 1],
        };
      }
    }

    return {
      errorMessage: `Name failed to parse correctly: ${name}. Employee will not be imported.`,
    };
  };

const parseNameField = (field: string) => (fieldValue: string | number | null) => {
  if (!fieldValue) {
    if (field === 'firstName' || field === 'lastName') {
      return {
        errorMessage: `${field} is missing for this row. Employee will not be imported.`,
      };
    }
    return {
      [field]: '',
    };
  }

  if (typeof fieldValue !== 'string' && Number.isNaN(fieldValue)) {
    return {
      errorMessage: `${field} failed to parse correctly: ${fieldValue}. Employee will not be imported.`,
    };
  }

  return {
    [field]: (removeInvalidAndCorrectCase([fieldValue.toString()])[0] || '').trim(),
  };
};

const MILLISECONDS_IN_A_DAY = 24 * 60 * 60 * 1000;
const MS_EPOCH_OFFSET = 25569; // MS Excel dates start on Jan 1 1900, whereas JS Dates start on Jan 1 1970
const MSDateParser = {
  fromOADate(oaDate: number) {
    const date = new Date((oaDate - MS_EPOCH_OFFSET) * MILLISECONDS_IN_A_DAY);
    const tz = date.getTimezoneOffset();
    return new Date((oaDate - MS_EPOCH_OFFSET + tz / (60 * 24)) * MILLISECONDS_IN_A_DAY);
  },
};

const parseDate = (dateField: string) => (dateFromData: number | string | null) => {
  if (!dateFromData) {
    return { [dateField]: '' };
  }

  const dateToFormat =
    typeof dateFromData === 'number' ? MSDateParser.fromOADate(dateFromData) : dateFromData;

  if (moment(dateToFormat).isValid()) {
    return { [dateField]: formatDate(dateToFormat) };
  }
  return { warningMessage: `${dateField} ${dateFromData} is an invalid date` };
};

const FIELD_PARSERS: { [fieldName: string]: { parser: Function; resultingFields: Array<string> } } =
  {
    name: {
      parser: parseFullName('firstName', 'lastName'),
      resultingFields: ['firstName', 'lastName'],
    },
    firstName: {
      parser: parseNameField('firstName'),
      resultingFields: ['firstName'],
    },
    lastName: {
      parser: parseNameField('lastName'),
      resultingFields: ['lastName'],
    },
    nickname: {
      parser: parseNameField('nickname'),
      resultingFields: ['nickname'],
    },
    supervisorFirstName: {
      parser: parseNameField('supervisorFirstName'),
      resultingFields: ['supervisorFirstName'],
    },
    supervisorLastName: {
      parser: parseNameField('supervisorLastName'),
      resultingFields: ['supervisorLastName'],
    },
    supervisorName: {
      parser: parseFullName('supervisorFirstName', 'supervisorLastName'),
      resultingFields: ['supervisorFirstName', 'supervisorLastName'],
    },
    phoneNumber: {
      parser: (phoneNumber: string | number | null) => {
        if (!phoneNumber) {
          return { phoneNumber: '' };
        }
        try {
          return { phoneNumber: convertPhoneNumberToE164(phoneNumber) };
        } catch (e) {
          return { warningMessage: `This phone number is invalid: ${phoneNumber}` };
        }
      },
      resultingFields: ['phoneNumber'],
    },
    email: {
      parser: (email: string | null) => {
        if (!email) {
          return { email: '' };
        }
        const trimmedEmail = email.trim();
        if (!isEmail(trimmedEmail)) {
          return { warningMessage: `This email is invalid: ${email}` };
        }
        return { email: trimmedEmail };
      },
      resultingFields: ['email'],
    },
    languagePreference: {
      parser: (languagePreference: string | null) => {
        if (!languagePreference) {
          return { languagePreference: '' };
        }
        let parsedLanguagePreference = languagePreference.trim();
        if (!['en', 'es'].includes(languagePreference)) {
          if (languagePreference.toLowerCase().includes('english')) {
            parsedLanguagePreference = 'en';
          } else if (languagePreference.toLowerCase().includes('spanish')) {
            parsedLanguagePreference = 'es';
          } else {
            parsedLanguagePreference = '';
          }
        }
        if (!parsedLanguagePreference) {
          return { warningMessage: `${languagePreference} is not a supported language for import` };
        }

        return {
          languagePreference: parsedLanguagePreference,
        };
      },
      resultingFields: ['languagePreference'],
    },
    startDate: {
      parser: parseDate('startDate'),
      resultingFields: ['startDate'],
    },
    birthdate: {
      parser: parseDate('birthdate'),
      resultingFields: ['birthdate'],
    },
  };

export class EmployeeDirectoryImportFileParser {
  static getFields = () => FIELDS;

  static getFieldMap = () => FIELD_MAP;

  static getInitialHeaderMapping = (headers: Array<string>) => {
    const columnMappingsInitialMappings: { [csvColumn: string]: string } = {};
    let initialUsedFields: { [fieldName: string]: boolean } = {};
    FIELDS.forEach((field) =>
      headers.forEach((header: string) => {
        if (initialUsedFields[field.fieldName] || columnMappingsInitialMappings[header]) {
          return;
        }
        if (field.fieldName.includes(header) || field.displayName.includes(header)) {
          columnMappingsInitialMappings[header] = field.fieldName;
          if (!field.allowMultipleSelection) {
            initialUsedFields = {
              ...initialUsedFields,
              [field.fieldName]: true,
            };
          }
          if (INCOMPATIBLE_FIELDS[field.fieldName]) {
            INCOMPATIBLE_FIELDS[field.fieldName].forEach((incompatibleField) => {
              initialUsedFields = {
                ...initialUsedFields,
                [incompatibleField]: true,
              };
            });
          }
        }
      }),
    );
    return {
      columnMappingsInitialMappings,
      initialUsedFields,
    };
  };

  static getUpdatedMapping = (
    selectedFieldName: string,
    usedFields: { [fieldName: string]: boolean },
    originalMapping: string | undefined,
  ) => {
    const field = FIELDS.find((f) => f.fieldName === selectedFieldName);
    if (!field) {
      return null;
    }

    let newUsedFields = {
      ...usedFields,
      [selectedFieldName]: !field.allowMultipleSelection,
    };
    if (INCOMPATIBLE_FIELDS[field.fieldName]) {
      INCOMPATIBLE_FIELDS[field.fieldName].forEach((incompatibleField) => {
        newUsedFields = {
          ...newUsedFields,
          [incompatibleField]: true,
        };
      });
    }
    if (originalMapping) {
      delete newUsedFields[originalMapping];
      if (INCOMPATIBLE_FIELDS[originalMapping]) {
        INCOMPATIBLE_FIELDS[originalMapping].forEach((incompatibleField) => {
          delete newUsedFields[incompatibleField];
        });
      }
    }

    return newUsedFields;
  };

  static isIgnored = (key: string, columnMappings: { [csvColumn: string]: string }) =>
    columnMappings[key] === 'ignore';

  static getParsedHeaderCell = (key: string, columnMappings: { [csvColumn: string]: string }) =>
    FIELD_PARSERS[columnMappings[key]] ? (
      FIELD_PARSERS[columnMappings[key]].resultingFields.map((rf) => (
        <TableHeaderCell>{FIELD_MAP[rf].displayName}</TableHeaderCell>
      ))
    ) : (
      <TableHeaderCell>{FIELD_MAP[columnMappings[key]].displayName}</TableHeaderCell>
    );

  static getParsedPreview = (
    row: any,
    key: string,
    columnMappings: { [csvColumn: string]: string },
  ) =>
    FIELD_PARSERS[columnMappings[key]] ? (
      parseValueAndDisplayInReactNode(row, key, FIELD_PARSERS[columnMappings[key]])
    ) : (
      <div>{row[key]}</div>
    );

  static getParsedValueCell = (
    row: any,
    key: string,
    columnMappings: { [csvColumn: string]: string },
  ) =>
    FIELD_PARSERS[columnMappings[key]] ? (
      parseValueAndDisplayInReactNode(row, key, FIELD_PARSERS[columnMappings[key]], false)
    ) : (
      <TableCell>{row[key]}</TableCell>
    );

  static getRowsWithErrors = (
    rows: Array<Record<string, any>>,
    columnMappings: { [csvColumn: string]: string },
  ) => rows.filter((row) => EmployeeDirectoryImportFileParser._rowHasErrors(row, columnMappings));

  static getRowsWithoutErrors = (
    rows: Array<Record<string, any>>,
    columnMappings: { [csvColumn: string]: string },
  ) => rows.filter((row) => !EmployeeDirectoryImportFileParser._rowHasErrors(row, columnMappings));

  private static _rowHasErrors = (
    row: Record<string, any>,
    columnMappings: { [csvColumn: string]: string },
  ) => {
    let hasError = false;
    Object.keys(row).forEach((key) => {
      if (!hasError && FIELD_PARSERS[columnMappings[key]]) {
        const { parser } = FIELD_PARSERS[columnMappings[key]];
        const parsingResult = parser(row[key]);
        if (parsingResult.errorMessage || parsingResult.warningMessage) {
          hasError = true;
        }
      }
    });
    return hasError;
  };

  static keyParsesIntoMultipleFields = (
    key: string,
    columnMappings: { [csvColumn: string]: string },
  ) =>
    FIELD_PARSERS[columnMappings[key]]
      ? FIELD_PARSERS[columnMappings[key]].resultingFields.length > 1
      : false;

  static getResultingFieldsForKey = (
    key: string,
    columnMappings: { [csvColumn: string]: string },
  ) =>
    FIELD_PARSERS[columnMappings[key]] ? FIELD_PARSERS[columnMappings[key]].resultingFields : [];
}

const parseValueAndDisplayInReactNode = (
  row: any,
  key: string,
  fieldParser: { parser: Function; resultingFields: Array<string> },
  includeRow = true,
) => {
  const { parser, resultingFields } = fieldParser;
  const parsingResult = parser(row[key]);
  if (resultingFields.length === 1) {
    let content;
    if (parsingResult.warningMessage) {
      content = (
        <Tooltip title={parsingResult.warningMessage}>
          <MaterialDesignIcon iconName={ICON_NAMES.INFO} />
        </Tooltip>
      );
    } else if (parsingResult.errorMessage) {
      content = (
        <Tooltip title={parsingResult.errorMessage}>
          <MaterialDesignIcon iconName={ICON_NAMES.WARNING} color="error" />
        </Tooltip>
      );
    } else {
      content = parsingResult[resultingFields[0]];
    }

    return includeRow ? <div>{content}</div> : <TableCell>{content}</TableCell>;
  }
  const RowComponent = includeRow ? TableRow : React.Fragment;
  return (
    <RowComponent>
      {parsingResult.warningMessage
        ? resultingFields.map(() => (
            <TableCell>
              <Tooltip title={parsingResult.warningMessage}>
                <MaterialDesignIcon iconName={ICON_NAMES.INFO} />
              </Tooltip>
            </TableCell>
          ))
        : null}
      {parsingResult.errorMessage
        ? resultingFields.map(() => (
            <TableCell>
              <Tooltip title={parsingResult.errorMessage}>
                <MaterialDesignIcon iconName={ICON_NAMES.WARNING} color="error" />
              </Tooltip>
            </TableCell>
          ))
        : null}
      {!parsingResult.warningMessage && !parsingResult.errorMessage
        ? Object.values(parsingResult).map((val: any) => <TableCell>{val}</TableCell>)
        : null}
    </RowComponent>
  );
};
